import { Routes } from '@angular/router';
import { HomeComponent } from './home/home.component';
import { authGuard } from '@core/guards/auth.guard';
import { platformGuard } from '@core/guards/platform.guard';
import { stoqaAiEnabledGuard } from '@core/guards/stoqa-ai-enabled.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [platformGuard],
    loadComponent: () =>
      import('./landing-page/landing-page.component').then((m) => m.LandingPageComponent),
    pathMatch: 'full',
    data: { screenName: 'welcome' },
  },
  // Full-screen routes (no layout)
  {
    path: 'welcome',
    loadComponent: () =>
      import('./landing-page/landing-page.component').then((m) => m.LandingPageComponent),
    data: { screenName: 'welcome' },
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
    data: { screenName: 'onboarding' },
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: 'payment',
    loadComponent: () => import('./payment/payment.component').then((m) => m.PaymentComponent),
    canActivate: [authGuard],
    data: { screenName: 'checkout' },
  },
  {
    path: 'payment/success',
    loadComponent: () =>
      import('./payment/payment-success.component').then((m) => m.PaymentSuccessComponent),
    data: { screenName: 'purchase_success' },
  },
  {
    path: 'payment/failed',
    loadComponent: () =>
      import('./payment/payment-failure.component').then((m) => m.PaymentFailureComponent),
    data: { screenName: 'checkout' },
  },
  {
    path: 'payment/moyasar-callback',
    loadComponent: () =>
      import('./payment/moyasar-callback.component').then((m) => m.MoyasarCallbackComponent),
    data: { screenName: 'checkout' },
  },
  {
    path: 'search',
    loadComponent: () => import('./search/search.component').then((m) => m.SearchComponent),
    data: { screenName: 'search' },
  },
  // Main layout routes (with header/sidebar/footer)
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: 'home',
        component: HomeComponent,
        data: { screenName: 'market' },
      },
      {
        path: 'market-summary',
        loadComponent: () =>
          import('./home/home-fullscreen-chart.component').then(
            (m) => m.HomeFullscreenChartComponent
          ),
        data: { screenName: 'market' },
      },
      {
        path: 'news',
        loadChildren: () => import('./news/news.routes').then((m) => m.NEWS_ROUTES),
        data: { screenName: 'news' },
      },
      {
        path: 'articles',
        loadChildren: () => import('./articles/articles.routes').then((m) => m.ARTICLES_ROUTES),
      },
      {
        path: 'subscription-plans',
        loadComponent: () =>
          import('./subscription-plans/subscription-plans.component').then(
            (m) => m.SubscriptionPlansComponent
          ),
        data: { screenName: 'pricing' },
      },
      {
        path: 'sectors',
        loadChildren: () => import('./sectors/sectors.routes').then((m) => m.SECTORS_ROUTES),
        canActivate: [authGuard],
      },
      {
        path: 'my-shares',
        loadComponent: () =>
          import('./my-shares/my-shares.component').then((m) => m.MySharesComponent),
        canActivate: [authGuard],
      },
      {
        path: 'wallets-management',
        loadComponent: () =>
          import('./wallets-management/wallets-management.component').then(
            (m) => m.WalletsManagementComponent
          ),
        canActivate: [authGuard],
        data: { screenName: 'portfolio_manage' },
      },
      {
        path: 'stock-screener',
        loadComponent: () =>
          import('./stock-screener/stock-screener.component').then((m) => m.StockScreenerComponent),
        canActivate: [authGuard],
        data: { screenName: 'screener' },
      },
      {
        path: 'ai-chat',
        loadChildren: () => import('./ai-chat/ai-chat.routes').then((m) => m.AI_CHAT_ROUTES),
        canActivate: [authGuard],
        canMatch: [stoqaAiEnabledGuard],
        data: { screenName: 'ai_chat' },
      },
      {
        path: 'watch-list',
        loadComponent: () =>
          import('./watch-list/watch-list.component').then((m) => m.WatchListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('./upcoming-events/upcoming-events.component').then(
            (m) => m.UpcomingEventsComponent
          ),
        canActivate: [authGuard],
        data: { screenName: 'calendar' },
      },
      {
        path: 'comparisons',
        loadComponent: () =>
          import('./comparisons/comparisons.component').then((m) => m.ComparisonsComponent),
        canActivate: [authGuard],
        data: { screenName: 'compare' },
      },
      {
        path: 'user-account',
        loadComponent: () =>
          import('./user-account/user-account.component').then((m) => m.UserAccountComponent),
        canActivate: [authGuard],
        data: { screenName: 'profile' },
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./notifications/notifications.component').then((m) => m.NotificationsComponent),
        canActivate: [authGuard],
        data: { screenName: 'notifications' },
      },
      {
        path: 'my-alerts',
        loadComponent: () =>
          import('./my-alerts/my-alerts.component').then((m) => m.MyAlertsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'support',
        loadComponent: () => import('./support/support.component').then((m) => m.SupportComponent),
        data: { screenName: 'support' },
      },
      {
        path: 'about-us',
        loadComponent: () =>
          import('./about-us/about-us.component').then((m) => m.AboutUsComponent),
        data: { screenName: 'about_us' },
      },
      {
        path: 'terms-conditions',
        loadComponent: () =>
          import('./terms-conditions/terms-conditions.component').then(
            (m) => m.TermsConditionsComponent
          ),
        data: { screenName: 'terms_conditions' },
      },
      {
        path: 'mobile-more',
        loadComponent: () =>
          import('./mobile-more-page/mobile-more-page.component').then(
            (m) => m.MobileMorePageComponent
          ),
        data: { screenName: 'more' },
      },
      {
        path: 'voucher/:voucherId',
        loadComponent: () =>
          import('./influencer-analytics/influencer-analytics.component').then(
            (m) => m.InfluencerAnalyticsComponent
          ),
        canActivate: [authGuard],
        data: { screenName: 'voucher_landing' },
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
